import React, { useState, useRef, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface TooltipProps {
  /** The content to trigger the tooltip (usually an icon or button) */
  trigger: React.ReactNode;
  /** The tooltip content */
  content: React.ReactNode;
  /** Tooltip position relative to trigger */
  position?: 'top' | 'bottom' | 'left' | 'right';
  /** Tooltip width (default: 'w-64') */
  width?: string;
  /** Whether tooltip is shown on hover (default: false, uses click) */
  hover?: boolean;
  /** Additional className for the trigger wrapper */
  className?: string;
  /** Whether to show close button (default: true for click mode, false for hover) */
  showCloseButton?: boolean;
}

const Tooltip: React.FC<TooltipProps> = ({
  trigger,
  content,
  position = 'top',
  width = 'w-64',
  hover = false,
  className = '',
  showCloseButton
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  // Auto-close on hover mode when mouse leaves
  useEffect(() => {
    if (hover && isOpen) {
      const handleMouseLeave = () => setIsOpen(false);
      const triggerElement = triggerRef.current;
      const tooltipElement = tooltipRef.current;

      if (triggerElement) {
        triggerElement.addEventListener('mouseleave', handleMouseLeave);
      }
      if (tooltipElement) {
        tooltipElement.addEventListener('mouseleave', handleMouseLeave);
      }

      return () => {
        if (triggerElement) {
          triggerElement.removeEventListener('mouseleave', handleMouseLeave);
        }
        if (tooltipElement) {
          tooltipElement.removeEventListener('mouseleave', handleMouseLeave);
        }
      };
    }
  }, [hover, isOpen]);

  // Close on click outside (for click mode)
  useEffect(() => {
    if (!hover && isOpen) {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          tooltipRef.current &&
          !tooltipRef.current.contains(event.target as Node) &&
          triggerRef.current &&
          !triggerRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [hover, isOpen]);

  const handleTrigger = () => {
    if (hover) {
      setIsOpen(true);
    } else {
      setIsOpen(!isOpen);
    }
  };

  const shouldShowCloseButton = showCloseButton !== undefined 
    ? showCloseButton 
    : !hover;

  const positionClasses = {
    top: 'bottom-full mb-2 left-1/2 transform -translate-x-1/2',
    bottom: 'top-full mt-2 left-1/2 transform -translate-x-1/2',
    left: 'right-full mr-2 top-1/2 transform -translate-y-1/2',
    right: 'left-full ml-2 top-1/2 transform -translate-y-1/2'
  };

  const arrowClasses = {
    top: 'top-full left-1/2 transform -translate-x-1/2 -mt-1 border-t-gray-900',
    bottom: 'bottom-full left-1/2 transform -translate-x-1/2 -mb-1 border-b-gray-900',
    left: 'left-full top-1/2 transform -translate-y-1/2 -ml-1 border-l-gray-900',
    right: 'right-full top-1/2 transform -translate-y-1/2 -mr-1 border-r-gray-900'
  };

  const arrowBorderClasses = {
    top: 'border-4 border-transparent border-t-gray-900',
    bottom: 'border-4 border-transparent border-b-gray-900',
    left: 'border-4 border-transparent border-l-gray-900',
    right: 'border-4 border-transparent border-r-gray-900'
  };

  return (
    <div className={`relative ${className}`}>
      <div
        ref={triggerRef}
        onMouseEnter={hover ? handleTrigger : undefined}
        onClick={!hover ? handleTrigger : undefined}
        className="inline-block"
      >
        {trigger}
      </div>
      {isOpen && (
        <div
          ref={tooltipRef}
          className={`absolute ${positionClasses[position]} ${width} bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900 text-xs rounded-lg p-3 shadow-lg z-50`}
          onMouseEnter={hover ? () => setIsOpen(true) : undefined}
        >
          <div className="relative">
            {content}
            {shouldShowCloseButton && (
              <button
                onClick={() => setIsOpen(false)}
                className="absolute -top-2 -right-2 text-white hover:text-faint focus:outline-none"
                aria-label="Close tooltip"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className={`absolute ${arrowClasses[position]}`}>
            <div className={arrowBorderClasses[position]}></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tooltip;







